import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

class RequestNotificationService {
  _stompClient;

  subscriptions;

  retryCount;

  _timeoutId;

  brokerUrl;

  topicUri;

  debug;

  _enabled;

  _explicitDisconnect;

  sockjs = {
    options: {},
  };

  stomp = {
    headers: {},
  };

  props = {
    onConnect: () => {},
    onDisconnect: () => {},
    getRetryInterval: (count) => {
      return 1000 * count;
    },
    options: {},
    headers: {},
    subscribeHeaders: {},
    autoReconnect: true,
    debug: false,
    heartbeat: 10000,
  };

  constructor() {
    if (!RequestNotificationService.instance) {
      this._stompClient = new Client();

      this.subscriptions = new Map();
      this.retryCount = 0;
      this.brokerUrl = process.env.REACT_APP_REQUESTS_SERVER_DOMAIN;
      this.topicUri = process.env.REACT_APP_REQUESTS_SERVER_TOPIC_URI;
      this.debug =
        process.env.REACT_APP_REQUESTS_SERVER_DEBUG &&
        JSON.parse(process.env.REACT_APP_REQUESTS_SERVER_DEBUG.toLowerCase());

      this._enabled =
        process.env.REACT_APP_REQUESTS_SERVER_ENABLED &&
        JSON.parse(process.env.REACT_APP_REQUESTS_SERVER_ENABLED.toLowerCase());

      this._initStompClient();

      RequestNotificationService.instance = this;
    }

    return RequestNotificationService.instance;
  }

  get connected() {
    return this._stompClient && this._stompClient.connected;
  }

  get explicitDisconnect() {
    return this._explicitDisconnect;
  }

  set explicitDisconnect(value) {
    this._explicitDisconnect = value;
  }

  /**
   * Disconnect STOMP client and disable all reconnect.
   *
   * @public
   */
  disconnect() {
    // On calling disconnect explicitly no effort will be made to reconnect
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
    this.explicitDisconnect = true;
    if (this.connected) {
      this.subscriptions.forEach((subid, topic) => {
        this._unsubscribe(topic);
      });
      this._stompClient.deactivate();
    }
  }

  /**
   * Connect to the server if not connected. Under normal circumstances component
   * will automatically try to connect to server. This method is mostly useful
   * after component is explicitly disconnected via {@link SocketManager#disconnect}.
   *
   * @public
   */
  connect() {
    this.explicitDisconnect = false;
    if (!this._stompClient || !(this._stompClient instanceof Client)) {
      this._initStompClient();
    }
    if (!this.connected) {
      this._connect();
    }
  }

  /**
   * Send message to the specified topic.
   *
   * @param {string} topic target topic to send message
   * @param {string} msg message to send
   * @param {Object} [opt_headers={}] additional headers for underlying STOMP client
   * @public
   */
  sendMessage(topic, msg, optHeaders = {}) {
    if (this.connected) {
      this._stompClient.publish({
        destination: topic,
        optHeaders,
        body: msg,
      });
    } else {
      throw new Error('Send error: SocketManager is disconnected');
    }
  }

  /**
   *
   * @param {*} topic
   * @param {*} onMessage
   */
  subscribe(topic, onMessage = () => {}, onSubscribe = () => {}) {
    if (!this.subscriptions.has(topic)) {
      const sub = this._stompClient.subscribe(
        `${this.topicUri}/${topic}`,
        (msg) => {
          this._log('onMessage', msg);
          onMessage(this._processMessage(msg.body), msg.headers.destination);
        },
        this.props.subscribeHeaders
      );
      this.subscriptions.set(topic, sub);
      onSubscribe(sub);
    }
  }

  _initStompClient() {
    if (this._enabled || !this.brokerUrl || !this.topicUri) {
      return;
    }

    this._stompClient.webSocketFactory = () => {
      if (WebSocket && this.brokerUrl.startsWith('ws')) {
        return new WebSocket(this.brokerUrl);
      }
      return new SockJS(this.brokerUrl, null, this.sockjs.options);
    };

    this._stompClient.configure({
      reconnectDelay: 0,
      connectHeaders: this.props.headers,
      heartbeatIncoming: Object.keys(this.props).includes('heartbeatIncoming')
        ? this.props.heartbeatIncoming
        : this.props.heartbeat,
      heartbeatOutgoing: Object.keys(this.props).includes('heartbeatOutgoing')
        ? this.props.heartbeatOutgoing
        : this.props.heartbeat,
      debug: this.debug ? (str) => console.log(new Date(), str) : () => {},
    });

    this._stompClient.onConnect = (frame) => {
      this._log('onConnect', frame);
      this.props.onConnect();
    };

    this._stompClient.onDisconnect = (frame) => {
      this._log('onDisconnect', frame);
      if (this.connected) {
        this._cleanUp();
        this.props.onDisconnect();
        this._log('Stomp client is successfully disconnected!');
      }
    };

    this._stompClient.onWebSocketClose = (event) => {
      if (this.connected) {
        this._log('onWebSocketClose', event);
        this._onConnectionFailure(event);
      }
    };

    this._stompClient.onWebSocketError = (event) => {
      this._log('onWebSocketError', event);
      this._onConnectionFailure(event);
    };

    this._connect();
  }

  _connect() {
    this._stompClient.activate();
  }

  _onConnectionFailure(event) {
    if (Object.keys(this.props).includes('onConnectFailure')) {
      this.props.onConnectFailure(event);
    }
    if (this.connected) {
      this._cleanUp();
      this.props.onDisconnect();
    }
    if (this.props.autoReconnect && !this.explicitDisconnect) {
      this._timeoutId = setTimeout(
        () => this._connect(),
        this.props.getRetryInterval(this.retryCount < 16 ? (this.retryCount += 1) : this.retryCount)
      );
      this._log(`Attempting to reconnect in ${this.retryCount} seconds.`);
    }
  }

  _unsubscribe(topic) {
    const sub = this.subscriptions.get(topic);
    sub.unsubscribe();
    this.subscriptions.delete(topic);
  }

  _processMessage(msgBody) {
    try {
      return JSON.parse(msgBody);
    } catch (e) {
      return msgBody;
    }
  }

  _log(msg, obj) {
    if (this.debug) {
      if (obj) {
        console.log(new Date(), msg, obj);
      } else {
        console.log(new Date(), msg);
      }
    }
  }

  _cleanUp() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
    this.retryCount = 0;
    this.subscriptions.clear();
  }
}

const requestNotificationService = new RequestNotificationService();

export default requestNotificationService;
